import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { asList, getJson, money, periodLabel } from '../helpers';
import { reportPath } from '../reports';
import { FEE_LEVEL_LINE_MARKUP_FEE_LINE, feeLevelLabel } from '../../utils/contractFees';

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });
}

function formatPct(value) {
  if (value === null || value === undefined) return '—';
  const pct = Number(value);
  if (Math.abs(pct - Math.round(pct)) < 0.05) return `${Math.round(pct)}%`;
  return `${pct.toFixed(1)}%`;
}

function severityClass(severity) {
  if (severity === 'high') return 'error';
  if (severity === 'medium') return 'project-tool';
  return 'closed';
}

function findingAmount(finding) {
  if (finding.amount === undefined || finding.amount === null) return '—';
  return finding.unit === 'percent' ? formatPct(finding.amount) : money(finding.amount);
}

export default function ProjectAnalysisReport({ projectVuid }) {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState('');
  const [projectQuery, setProjectQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(Boolean(projectVuid));
  const [error, setError] = useState('');
  const projectSearchTimer = useRef(null);

  const searchProjects = (q) => {
    getJson('/api/projects', {
      page: 1,
      per_page: 25,
      lite: 1,
      q: (q || '').trim() || undefined,
    })
      .then((data) => setProjects(asList(data).filter((row) => row.status !== 'cancelled')))
      .catch(() => setProjects([]));
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getJson('/api/accounting-periods').catch(() => []),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ]).then(([periodData, project]) => {
      if (cancelled) return;
      const sorted = sortPeriods(asList(periodData));
      setPeriods(sorted);
      const openPeriod = sorted.find((period) => period.status === 'open');
      setSelectedPeriod((openPeriod || sorted[0] || {}).vuid || '');
      if (project?.project_name) setProjectName(project.project_name);
      if (!projectVuid) searchProjects('');
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Unable to load report setup');
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    if (!projectVuid) {
      setAnalysis(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    const params = { project_vuid: projectVuid };
    if (selectedPeriod) params.accounting_period_vuid = selectedPeriod;
    getJson('/api/project-analysis', params)
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.projects) ? data.projects : [];
        setAnalysis(rows[0] || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setAnalysis(null);
        setError(err.response?.data?.error || 'Unable to load project analysis');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid, selectedPeriod]);

  const fee = analysis?.fee;
  const findings = analysis?.findings || [];
  const counts = analysis?.finding_counts || {};

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            Project Analysis
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            Project Analysis
          </>
        )}
      </div>
      <div className="pc-title-row">
        <h1>Project Analysis</h1>
      </div>
      <div className="pc-toolbar">
        {!projectVuid ? (
          <label className="pc-toolbar-label">
            Project
            <input
              value={projectQuery}
              placeholder="Search projects"
              onChange={(event) => {
                const value = event.target.value;
                setProjectQuery(value);
                if (projectSearchTimer.current) clearTimeout(projectSearchTimer.current);
                projectSearchTimer.current = setTimeout(() => searchProjects(value), 200);
              }}
            />
            <select
              value=""
              onChange={(event) => {
                if (event.target.value) {
                  navigate(reportPath(event.target.value, 'project-analysis'));
                }
              }}
            >
              <option value="">Select a project</option>
              {projects.map((project) => (
                <option key={project.vuid} value={project.vuid}>
                  {project.project_number} {project.project_name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="pc-toolbar-label">
          As of period
          <select value={selectedPeriod} onChange={(event) => setSelectedPeriod(event.target.value)}>
            {periods.length === 0 ? <option value="">No periods</option> : null}
            {periods.map((period) => (
              <option key={period.vuid} value={period.vuid}>
                {periodLabel(period)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <div className="pc-card"><div className="pc-empty">Analyzing project…</div></div>
      ) : error ? (
        <div className="pc-card"><div className="pc-empty">{error}</div></div>
      ) : !projectVuid ? (
        <div className="pc-card"><div className="pc-empty">Select a project to run analysis.</div></div>
      ) : !analysis ? (
        <div className="pc-card"><div className="pc-empty">No project analysis data.</div></div>
      ) : (
        <div className="pc-sections">
          <div className="pc-grid">
            <div className="pc-card pc-stat">
              <div className="pc-label">Total Contract</div>
              <div className="pc-value">{money(analysis.total_contract)}</div>
            </div>
            <div className="pc-card pc-stat">
              <div className="pc-label">Current Budget</div>
              <div className="pc-value">{money(analysis.current_budget)}</div>
            </div>
            <div className="pc-card pc-stat">
              <div className="pc-label">Costs / Billings</div>
              <div className="pc-value">{money(analysis.costs_to_date)}</div>
              <div className="pc-sub">{money(analysis.billings_to_date)} billed</div>
            </div>
            <div className="pc-card pc-stat">
              <div className="pc-label">Over / Under Billing</div>
              <div className="pc-value">{money(analysis.over_under_billing)}</div>
              <div className="pc-sub">
                {formatPct(analysis.percent_complete)} complete · {formatPct(analysis.percent_billed)} billed
              </div>
            </div>
          </div>

          {fee ? (
            <div className="pc-card pc-report-card">
              <div className="pc-story">
                <strong>{feeLevelLabel(fee.fee_level || analysis.fee_level)}</strong>
                <div className="pc-sub">
                  {(fee.fee_level || analysis.fee_level) === FEE_LEVEL_LINE_MARKUP_FEE_LINE
                    ? 'Per-line markups are billed on the dedicated fee cost code. Labor billed from a rate schedule is excluded from fee unless the contract line is set to include it.'
                    : fee.uses_stored_fee_percent
                    ? `Contract fee ${formatPct(fee.original_fee_percent)}. Expected change-order fees use this stored rate.`
                    : `Original ${formatPct(fee.original_fee_percent)} → current ${formatPct(fee.current_fee_percent)}.`}
                  {' '}
                  Fee is {money(fee.original_fee)} on the original contract and {money(fee.current_fee)} including approved change orders.
                </div>
              </div>
              {(fee.change_orders || []).length > 0 ? (
                <div className="pc-table-scroll">
                  <table className="pc-table pc-report">
                    <thead>
                      <tr>
                        <th>CO</th>
                        <th>Description</th>
                        <th className="pc-right">Contract Change</th>
                        <th className="pc-right">Fee</th>
                        <th className="pc-right">Expected Fee</th>
                        <th className="pc-right">Gap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fee.change_orders.map((row) => (
                        <tr key={row.vuid}>
                          <td>
                            {row.vuid ? (
                              <Link to={`/procore/projects/${projectVuid}/change-orders/external/${row.vuid}`}>
                                {row.change_order_number}
                              </Link>
                            ) : (
                              row.change_order_number
                            )}
                          </td>
                          <td>{row.description || '—'}</td>
                          <td className="pc-right">{money(row.contract_change)}</td>
                          <td className="pc-right">{money(row.fee_amount)}</td>
                          <td className="pc-right">{money(row.expected_fee)}</td>
                          <td className="pc-right">{money(row.fee_gap)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="pc-card pc-report-card">
            <div className="pc-story">
              <strong>Findings</strong>
              <div className="pc-sub">
                {counts.high || 0} high · {counts.medium || 0} medium · {counts.low || 0} low
              </div>
            </div>
            {findings.length === 0 ? (
              <div className="pc-empty">No anomalies flagged for this project.</div>
            ) : (
              <div className="pc-table-scroll">
                <table className="pc-table pc-report">
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Category</th>
                      <th>Finding</th>
                      <th className="pc-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {findings.map((finding) => (
                      <tr key={finding.id}>
                        <td>
                          <span className={`pc-pill ${severityClass(finding.severity)}`}>
                            {finding.severity}
                          </span>
                        </td>
                        <td>{finding.category || '—'}</td>
                        <td>
                          {finding.title}
                          <div className="pc-sub">{finding.detail}</div>
                        </td>
                        <td className="pc-right">{findingAmount(finding)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
